import api from './api';

/**
 * Joint account API client.
 * - FAMILY: shared wallet / minor aliases (create via /joint-accounts page).
 * - BUSINESS: member/alias shell for a credentialed business mailbox — use My Profile → Additional Members
 *   (or POST create while logged in as the business mailbox username); not from personal login.
 */

// Types
export interface JointAccountSummary {
  jointAccountId: string;
  accountName: string;
  accountType: 'BUSINESS' | 'FAMILY';
  availableBalance: number;
  memberCount: number;
  userRole: string;
  lastActivity: string;
}

export interface JointAccountDetail {
  jointAccountId: string;
  primaryHolderUserId: string;
  primaryHolderUsername: string;
  accountType: 'BUSINESS' | 'FAMILY';
  accountName: string;
  description?: string;
  isFamilyAccount: boolean;
  requireParentalApproval: boolean;
  defaultDailyLimit?: number;
  defaultPerTransactionLimit?: number;
  availableBalance: number;
  actualBalance: number;
  memberCount: number;
  status: number;
  created: string;
  lastModified: string;
}

export interface JointAccountBalance {
  jointAccountId: string;
  accountName: string;
  availableBalance: number;
  actualBalance: number;
  pendingBalance: number;
  dailyLimit?: number;
  dailySpent?: number;
  dailyRemaining?: number;
  weeklyLimit?: number;
  weeklySpent?: number;
  weeklyRemaining?: number;
  monthlyLimit?: number;
  monthlySpent?: number;
  monthlyRemaining?: number;
}

export interface JointAccountMember {
  membershipId: string;
  memberUserId: string;
  username: string;
  displayName?: string;
  invitedAs?: string;
  role: 'PRIMARY' | 'ADMIN' | 'MEMBER' | 'MINOR';
  canViewBalance: boolean;
  canViewHistory: boolean;
  canSpend: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  canInviteMembers: boolean;
  canEditSettings: boolean;
  dailyLimit?: number;
  weeklyLimit?: number;
  monthlyLimit?: number;
  perTransactionLimit?: number;
  currentDailySpent: number;
  currentWeeklySpent: number;
  currentMonthlySpent: number;
  birthDate?: string;
  requiresApproval: boolean;
  status: number;
  joinedDate: string;
  lastAccess?: string;
  /** Set when this membership record is backed by a joint account alias (tbJointAccountAliases). Members with an aliasId are displayed in the alias list, not the owner list. */
  aliasId?: string;
  isMinor?: boolean;
}

export interface JointAccountTransaction {
  transactionId: string;
  memberUserId: string;
  memberUsername: string;
  memberDisplayName?: string;
  transactionType: 'DEPOSIT' | 'SPEND' | 'WITHDRAW' | 'TRANSFER' | 'ALLOWANCE';
  amount: number;
  description?: string;
  recipient?: string;
  balanceBefore?: number;
  balanceAfter?: number;
  requiredApproval: boolean;
  approved?: boolean;
  approvedByUserId?: string;
  approvedByUsername?: string;
  approvalDate?: string;
  approvalNotes?: string;
  status: number;
  statusText: string;
  created: string;
}

export interface AllowanceSchedule {
  scheduleId: string;
  memberUserId: string;
  memberUsername: string;
  memberDisplayName?: string;
  scheduleName?: string;
  frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  amount: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  startDate: string;
  endDate?: string;
  nextRun?: string;
  lastRun?: string;
  totalDistributed: number;
  totalAmountDistributed: number;
  isActive: boolean;
}

// Request types
export interface CreateJointAccountRequest {
  accountType: 'BUSINESS' | 'FAMILY';
  accountName: string;
  description?: string;
  isFamilyAccount: boolean;
  requireParentalApproval: boolean;
  defaultDailyLimit?: number;
  defaultPerTransactionLimit?: number;
}

export interface InviteMemberRequest {
  username: string;
  role: 'ADMIN' | 'MEMBER' | 'MINOR';
  displayName?: string;
  canSpend: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  canInviteMembers: boolean;
  canEditSettings: boolean;
  dailyLimit?: number;
  weeklyLimit?: number;
  monthlyLimit?: number;
  perTransactionLimit?: number;
  birthDate?: string;
  requiresApproval: boolean;
  password?: string;
}

export interface UpdateMemberRequest {
  displayName?: string;
  role?: string;
  canSpend?: boolean;
  canDeposit?: boolean;
  canWithdraw?: boolean;
  canInviteMembers?: boolean;
  canEditSettings?: boolean;
  dailyLimit?: number;
  weeklyLimit?: number;
  monthlyLimit?: number;
  perTransactionLimit?: number;
  requiresApproval?: boolean;
  newPassword?: string;
  /** Tab keys the minor is allowed to see (undefined = don't change; empty array = all) */
  allowedTabs?: string[];
}

// Joint Account Alias types (new system)
export interface JointAccountAlias {
  aliasId: string;
  jointAccountId: string;
  jointAccountName: string;
  linkedUserId: string;
  aliasUsername: string;
  originalInvitedUsername: string;
  displayName?: string;
  isMinor: boolean;
  parentUserId?: string;
  parentUsername?: string;
  canViewBalance: boolean;
  canViewHistory: boolean;
  canSpend: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  canInviteMembers: boolean;
  canEditSettings: boolean;
  dailySpendLimit?: number;
  weeklySpendLimit?: number;
  monthlySpendLimit?: number;
  perTransactionLimit?: number;
  currentDailySpent: number;
  currentWeeklySpent: number;
  currentMonthlySpent: number;
  profilePublic: boolean;
  allowedTabs?: string[];
  role?: string;
  status: number;
  statusText: string;
  created: string;
  lastLogin?: string;
}

export interface InviteAdultMemberRequest {
  originalUsername: string;
  newAliasUsername: string;
  displayName?: string;
  role: 'ADMIN' | 'MEMBER';
  canSpend: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  canInviteMembers: boolean;
  canEditSettings: boolean;
  dailySpendLimit?: number;
  weeklySpendLimit?: number;
  monthlySpendLimit?: number;
  perTransactionLimit?: number;
  allowedTabs?: string[];
}

export interface CreateMinorAliasRequest {
  aliasUsername: string;
  displayName?: string;
  password?: string;
  dailySpendLimit: number;
  weeklySpendLimit?: number;
  monthlySpendLimit?: number;
  perTransactionLimit?: number;
}

export interface UpdateAliasRequest {
  newAliasUsername?: string;
  displayName?: string;
  newPassword?: string;
  profilePublic?: boolean;
  allowedTabs?: string[];
  canSpend?: boolean;
  canDeposit?: boolean;
  canWithdraw?: boolean;
  canInviteMembers?: boolean;
  canEditSettings?: boolean;
  dailySpendLimit?: number;
  monthlySpendLimit?: number;
  perTransactionLimit?: number;
  role?: string;
}

export interface UpdateMinorAliasRequest {
  newAliasUsername?: string;
  displayName?: string;
  newPassword?: string;
  dailySpendLimit?: number;
  weeklySpendLimit?: number;
  monthlySpendLimit?: number;
  perTransactionLimit?: number;
  enabled?: boolean;
}

export interface MyJointAccounts {
  minorAccounts: MinorAccount[];
  memberships: JointAccountMembership[];
}

export interface MinorAccount {
  aliasId: string;
  jointAccountId: string;
  jointAccountName: string;
  aliasUsername: string;
  displayName?: string;
  dailySpendLimit: number;
  currentDailySpent: number;
  lastActivity?: string;
  isEnabled: boolean;
}

export interface JointAccountMembership {
  jointAccountId: string;
  jointAccountName: string;
  accountType: string;
  aliasId: string;
  myAliasUsername: string;
  role: string;
  status: number;
}

export interface DepositRequest {
  amount: number;
  description?: string;
  fromPersonalAccount: boolean;
}

export interface SpendRequest {
  amount: number;
  description?: string;
  recipient?: string;
}

export interface WithdrawRequest {
  amount: number;
  description?: string;
}

export interface CreateAllowanceRequest {
  memberUserId: string;
  scheduleName?: string;
  frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  amount: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  startDate?: string;
  endDate?: string;
}

// API Functions
export const jointAccountService = {
  async getMyAccounts(): Promise<JointAccountSummary[]> {
    return await api.get<JointAccountSummary[]>('/api/joint-accounts') || [];
  },

  async getAccount(jointAccountId: string): Promise<JointAccountDetail> {
    return api.get<JointAccountDetail>(`/api/joint-accounts/${jointAccountId}`);
  },

  async createAccount(request: CreateJointAccountRequest) {
    return api.post<{ success: boolean; jointAccountId?: string; message?: string }>('/api/joint-accounts', request);
  },

  async updateAccount(jointAccountId: string, updates: Partial<CreateJointAccountRequest>) {
    return api.put<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}`, updates);
  },

  async closeAccount(jointAccountId: string) {
    return api.delete<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}`);
  },

  async getBalance(jointAccountId: string): Promise<JointAccountBalance> {
    return api.get<JointAccountBalance>(`/api/joint-accounts/${jointAccountId}/balance`);
  },

  async getMembers(jointAccountId: string): Promise<JointAccountMember[]> {
    return api.get<JointAccountMember[]>(`/api/joint-accounts/${jointAccountId}/members`);
  },

  async inviteMember(jointAccountId: string, request: InviteMemberRequest) {
    return api.post<{ success: boolean; membershipId?: string; message?: string }>(`/api/joint-accounts/${jointAccountId}/members`, request);
  },

  async updateMember(jointAccountId: string, membershipId: string, request: UpdateMemberRequest) {
    return api.put<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}/members/${membershipId}`, request);
  },

  async removeMember(jointAccountId: string, membershipId: string) {
    return api.delete<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}/members/${membershipId}`);
  },

  async deposit(jointAccountId: string, request: DepositRequest) {
    return api.post<{ success: boolean; transactionId?: string; message?: string }>(`/api/joint-accounts/${jointAccountId}/deposit`, request);
  },

  async spend(jointAccountId: string, request: SpendRequest) {
    return api.post<{ success: boolean; transactionId?: string; pendingApproval?: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}/spend`, request);
  },

  async withdraw(jointAccountId: string, request: WithdrawRequest) {
    return api.post<{ success: boolean; transactionId?: string; message?: string }>(`/api/joint-accounts/${jointAccountId}/withdraw`, request);
  },

  async getHistory(jointAccountId: string, limit = 50, offset = 0): Promise<JointAccountTransaction[]> {
    return api.get<JointAccountTransaction[]>(`/api/joint-accounts/${jointAccountId}/history`, {
      params: { limit, offset }
    });
  },

  async getPendingApprovals(jointAccountId: string): Promise<JointAccountTransaction[]> {
    return api.get<JointAccountTransaction[]>(`/api/joint-accounts/${jointAccountId}/pending-approvals`);
  },

  async approveTransaction(jointAccountId: string, transactionId: string, approve: boolean, notes?: string) {
    return api.post<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}/approve/${transactionId}`, {
      approve,
      notes
    });
  },

  async getAllowances(jointAccountId: string): Promise<AllowanceSchedule[]> {
    return api.get<AllowanceSchedule[]>(`/api/joint-accounts/${jointAccountId}/allowances`);
  },

  async createAllowance(jointAccountId: string, request: CreateAllowanceRequest) {
    return api.post<{ success: boolean; scheduleId?: string; message?: string }>(`/api/joint-accounts/${jointAccountId}/allowances`, request);
  },

  async updateAllowance(jointAccountId: string, scheduleId: string, updates: Partial<CreateAllowanceRequest>) {
    return api.put<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}/allowances/${scheduleId}`, updates);
  },

  async deleteAllowance(jointAccountId: string, scheduleId: string) {
    return api.delete<{ success: boolean; message?: string }>(`/api/joint-accounts/${jointAccountId}/allowances/${scheduleId}`);
  },

  // ============================================
  // Alias-Based Member Management (New System)
  // ============================================

  async inviteWithAlias(jointAccountId: string, request: InviteAdultMemberRequest) {
    return api.post<{ success: boolean; aliasId?: string; message?: string }>(
      `/api/joint-accounts/${jointAccountId}/invite-with-alias`, 
      request
    );
  },

  async createMinorAlias(jointAccountId: string, request: CreateMinorAliasRequest) {
    return api.post<{ success: boolean; aliasId?: string; message?: string }>(
      `/api/joint-accounts/${jointAccountId}/create-minor`, 
      request
    );
  },

  async getAliases(jointAccountId: string): Promise<JointAccountAlias[]> {
    return api.get<JointAccountAlias[]>(`/api/joint-accounts/${jointAccountId}/aliases`);
  },

  async updateAlias(aliasId: string, request: UpdateAliasRequest) {
    return api.put<{ success: boolean; message?: string }>(
      `/api/joint-accounts/aliases/${aliasId}`, 
      request
    );
  },

  async updateMinorAlias(aliasId: string, request: UpdateMinorAliasRequest) {
    return api.put<{ success: boolean; message?: string }>(
      `/api/joint-accounts/aliases/${aliasId}/minor`, 
      request
    );
  },

  async disableAlias(aliasId: string) {
    return api.delete<{ success: boolean; message?: string }>(
      `/api/joint-accounts/aliases/${aliasId}`
    );
  },

  // ============================================
  // My Profile - Joint Accounts Tab
  // ============================================

  async getMyJointAccounts(): Promise<MyJointAccounts> {
    return api.get<MyJointAccounts>('/api/profile/joint-accounts');
  },

  async getMyMinorAccounts(): Promise<MinorAccount[]> {
    return api.get<MinorAccount[]>('/api/profile/joint-accounts/minors');
  },
};

export default jointAccountService;
